import {
  Box,
  FieldText,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  Text,
} from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import type { OpeningHours } from 'lib-shared-types-api';
import { ChangeEvent, useCallback, useState } from 'react';
import type { ImageFile } from 'react-dropzone';

import { useUpload } from 'apis/uploads';
import dialogStyles from 'components/shared/announcementPicker/dialog.module.scss';
import { AudioPlayer } from 'components/shared/audioPlayer/audioPlayer';
import { RecordPlayer } from 'components/shared/recordPlayer/recordPlayer';
import { UploadDropzone } from 'components/shared/uploadDropzone';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { bytesToSize } from 'utils/bytesToSize';

import styles from './audioMessageModal.module.scss';

const maxFileSize = 20 * 1024 * 1024; // ~20MByte
const allowedFileTypes = 'audio/*';

type Props = {
  modalType: AudioMessageModalType;
  onClose: () => void;
  onUploadFinish: (file: null | File, url: string) => void;
  openingHourType: OpeningHours['opening_hour_type'];
  title: string;
};

export type AudioMessageModalType = 'record' | 'upload';

function getFile(file: File, name: string) {
  return new File([file], name, { type: file.type });
}

export const AudioMessageModal = ({
  modalType,
  onClose,
  onUploadFinish,
  openingHourType,
  title,
}: Props): JSX.Element => {
  const [file, setFile] = useState<null | ImageFile>(null);
  const [name, setName] = useState('');

  const [upload, { asyncStatus }] = useUpload();
  const notify = useNotifications();

  const handleOnDrop = useCallback((file: File) => {
    setFile(file);
  }, []);

  const handleDropReject = useCallback(
    (file: File, reason: 'size' | 'type') => {
      switch (reason) {
        case 'size':
          notify(
            {
              title: 'File too large',
              text: `Maximum upload file size is ${bytesToSize(
                maxFileSize,
              )}. File size on "${file.name}" is ${bytesToSize(file.size)}`,
              time: 5000,
            },
            NotificationType.Error,
          );
          break;
        case 'type':
          notify(
            {
              title: 'Unsupported file type',
              text: 'Only audio files are supported',
              time: 5000,
            },
            NotificationType.Error,
          );
          break;
      }
    },
    [notify],
  );

  const handleNameChange = useCallback(
    (event?: ChangeEvent<HTMLInputElement>) =>
      void setName(event?.target.value || ''),
    [],
  );

  const handleConfirmClick = useCallback(() => {
    if (!file) return;

    ampli.businessHoursScheduleAudioMessageFormSubmitted({
      openingHourType,
      type: modalType,
    });

    upload({
      file: getFile(file, name),
      type: 'announcement',
    }).then((state) => {
      switch (state.asyncStatus) {
        case 'fulfilled':
          onUploadFinish(getFile(file, name), state.url);
          break;
        case 'rejected':
          notify(
            { title: 'Failed to fetch audio items, please try again' },
            NotificationType.Error,
          );
          break;
      }
    });
  }, [file, modalType, name, openingHourType, upload, notify, onUploadFinish]);

  const handleClose = useCallback(() => {
    ampli.businessHoursScheduleAudioMessageFormCancelled({
      openingHourType,
      type: modalType,
    });
    onClose();
  }, [modalType, openingHourType, onClose]);

  const isRecordModal = modalType === 'record';
  const isAudioPlayerShown = !isRecordModal && file;
  const isUploadShown = !isRecordModal && !file;

  return (
    <Modal isOpen onClose={handleClose}>
      <Box width="600px">
        <ModalHead onClose={handleClose} title={title} />
        <ModalBody>
          <Text.Paragraph className={styles.description}>
            {openingHourType === 'regular'
              ? 'This audio message can be played when your customers call you outside your standard business hours.'
              : 'This audio message can be played when your customers call you outside these business hours.'}
          </Text.Paragraph>

          {isRecordModal && <RecordPlayer onChange={setFile} name={name} />}

          {isAudioPlayerShown && (
            <AudioPlayer
              src={file.preview}
              filename={title}
              onDelete={() => setFile(null)}
            />
          )}

          {isUploadShown && (
            <UploadDropzone
              maxFileSize={maxFileSize}
              className={dialogStyles.dropzone}
              allowedFileTypes={allowedFileTypes}
              onDrop={handleOnDrop}
              onReject={handleDropReject}
            />
          )}
          <FieldText
            className={styles.nameField}
            input={{
              value: name,
              name: 'name',
              onChange: handleNameChange,
            }}
            label="Name"
            placeholder={
              openingHourType === 'regular'
                ? 'E.g. After hours message'
                : 'E.g. Happy New Year, Summer holidays message etc.'
            }
            required
          />
        </ModalBody>
        <ModalFoot
          cancelOnClick={handleClose}
          confirmOnClick={handleConfirmClick}
          confirmProps={{
            disabled: !file || !name,
            isLoading: asyncStatus === 'pending',
          }}
        />
      </Box>
    </Modal>
  );
};
